import { useCallback, useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import {
  LockedAutocomplete,
  type AutocompleteOption,
} from "@/components/LockedAutocomplete";
import { ProductBarcodeLookupField } from "@/components/pos/ProductBarcodeLookupField";
import { api } from "@/lib/apiClient";
import { UNIT_LABELS } from "@/lib/articleConstants";
import { accountSource } from "@/lib/autocompleteSources";
import { todayLocal } from "@/lib/bookingDates";
import { formatCurrency, netFromGross, parseAmount } from "@/lib/money";
import {
  AccountTaxRateNotice,
  accountHasTaxRate,
} from "@/pages/stammdaten/AccountTaxRateNotice";
import type {
  Account,
  Article,
  ArticleCategory,
  ProductLookupResult,
  SuggestedArticleNumber,
} from "@/types/camping";

const EMPTY_FORM = {
  category_id: "",
  article_number: "",
  name: "",
  unit: "piece",
  account_id: "",
  barcode: "",
  purchase_price: "",
  initial_sale_price: "",
  initial_valid_from: todayLocal(),
  track_inventory: true,
  batch_number: "",
  batch_quantity: "",
  batch_expires_at: "",
};

export interface QuickSaleCreateArticleModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (article: Article) => void;
}

export function QuickSaleCreateArticleModal({
  open,
  onClose,
  onCreated,
}: QuickSaleCreateArticleModalProps) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountOption, setAccountOption] = useState<AutocompleteOption<Account> | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);

  const loadAccountOptions = useCallback(
    (query: string) => accountSource(accounts, query),
    [accounts],
  );

  const loadCategories = useCallback(() => {
    api
      .get<ArticleCategory[]>("/api/v1/article-categories")
      .then((rows) => setCategories(rows.filter((c) => c.is_active && c.for_quick_sale)))
      .catch(() => setCategories([]));
  }, []);

  const suggestNumber = useCallback(async (categoryId: string) => {
    if (!categoryId) return;
    setSuggesting(true);
    try {
      const result = await api.get<SuggestedArticleNumber>(
        `/api/v1/article-categories/${categoryId}/suggest-number`,
      );
      setForm((f) => ({ ...f, article_number: result.suggested_number }));
    } catch {
      /* optional */
    } finally {
      setSuggesting(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY_FORM, initial_valid_from: todayLocal() });
    setAccountOption(null);
    setError(null);
    setSubmitting(false);
    loadCategories();
    api
      .get<Account[]>("/api/v1/accounts")
      .then(setAccounts)
      .catch(() => setAccounts([]));
  }, [open, loadCategories]);

  useEffect(() => {
    if (!open || !form.category_id) return;
    void suggestNumber(form.category_id);
  }, [open, form.category_id, suggestNumber]);

  function applyLookup(result: ProductLookupResult) {
    setForm((f) => ({
      ...f,
      barcode: result.barcode,
      name: result.name ?? f.name,
    }));
  }

  const selectedAccount = accounts.find((account) => account.id === form.account_id) ?? null;
  const taxRate = accountHasTaxRate(selectedAccount)
    ? Number(selectedAccount?.tax_rate_percent)
    : 0;
  const saleGross = parseAmount(form.initial_sale_price);
  const saleNet =
    saleGross != null
      ? Math.round(netFromGross(saleGross, taxRate) * 100) / 100
      : null;
  const selectedCategory = categories.find((c) => c.id === form.category_id);
  const batchQty = parseAmount(form.batch_quantity);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.category_id) {
      setError("Bitte eine Schnellverkauf-Artikelkategorie wählen.");
      return;
    }
    if (!form.account_id || !accountHasTaxRate(selectedAccount)) {
      setError(
        "Bitte ein Buchungskonto mit Mehrwertsteuersatz wählen. Der Satz kommt vom Konto.",
      );
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const purchaseNet = parseAmount(form.purchase_price);
      const body: Record<string, unknown> = {
        category_id: form.category_id,
        account_id: form.account_id,
        article_number: form.article_number.trim(),
        name: form.name.trim(),
        unit: form.unit,
        barcode: form.barcode.trim() || null,
        purchase_price: purchaseNet,
        track_inventory: form.track_inventory,
        initial_valid_from: form.initial_valid_from,
        initial_sale_price: saleNet,
      };
      if (form.track_inventory && batchQty != null && batchQty > 0) {
        body.initial_batch = {
          batch_number: form.batch_number.trim() || null,
          quantity: batchQty,
          purchase_price: purchaseNet,
          expires_at: form.batch_expires_at || null,
        };
      } else if (batchQty != null && batchQty > 0) {
        body.stock_quantity = batchQty;
      }

      const article = await api.post<Article>("/api/v1/pos/articles", body);
      onCreated(article);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Anlegen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Neuer Artikel (Schnellverkauf)"
      description="Shop-Artikel mit Barcode, EK/VK, Lager und optionaler Charge"
      size="lg"
    >
      {categories.length === 0 ? (
        <p className="text-sm text-label-secondary">
          Es ist keine Artikelkategorie für den Schnellverkauf aktiv. Legen Sie unter{" "}
          <strong>Stammdaten → Artikelkategorien</strong> eine Kategorie an und aktivieren Sie
          „Für Schnellverkauf“.
        </p>
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-6">
          <ProductBarcodeLookupField
            barcode={form.barcode}
            onBarcodeChange={(barcode) => setForm((f) => ({ ...f, barcode }))}
            onLookupResult={applyLookup}
            disabled={submitting}
          />

          <fieldset className="space-y-4">
            <legend className="text-sm font-semibold text-label">Stammdaten</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm sm:col-span-2">
                Artikelkategorie (Schnellverkauf)
                <select
                  required
                  className="eh-select-input"
                  value={form.category_id}
                  onChange={(e) => {
                    const category_id = e.target.value;
                    setForm((f) => ({ ...f, category_id, article_number: "" }));
                  }}
                >
                  <option value="">Bitte wählen…</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.article_number_pattern})
                    </option>
                  ))}
                </select>
                {selectedCategory && suggesting ? (
                  <span className="text-xs text-label-secondary">Nummer wird vorgeschlagen…</span>
                ) : null}
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Artikelnummer
                <input
                  required
                  className="eh-input font-mono"
                  value={form.article_number}
                  onChange={(e) => setForm((f) => ({ ...f, article_number: e.target.value }))}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Bezeichnung
                <input
                  required
                  className="eh-input"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Einheit
                <select
                  className="eh-select-input"
                  value={form.unit}
                  onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
                >
                  {Object.entries(UNIT_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-label">Preise</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm">
                EK netto (Einkauf)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="eh-input"
                  placeholder="optional"
                  value={form.purchase_price}
                  onChange={(e) => setForm((f) => ({ ...f, purchase_price: e.target.value }))}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                VK brutto (Verkauf)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  className="eh-input"
                  value={form.initial_sale_price}
                  onChange={(e) => setForm((f) => ({ ...f, initial_sale_price: e.target.value }))}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                VK gültig ab
                <input
                  type="date"
                  className="eh-input"
                  value={form.initial_valid_from}
                  onChange={(e) => setForm((f) => ({ ...f, initial_valid_from: e.target.value }))}
                />
              </label>
            </div>
            <LockedAutocomplete
              className="sm:col-span-2"
              label="Buchungskonto"
              required
              placeholder="Kontonummer oder Bezeichnung suchen…"
              value={accountOption}
              loadOptions={loadAccountOptions}
              onChange={(option) => {
                setAccountOption(option);
                setForm((f) => ({ ...f, account_id: option?.value.id ?? "" }));
              }}
            />
            <div className="sm:col-span-2">
              <AccountTaxRateNotice account={selectedAccount} />
            </div>
            {saleNet != null ? (
              <p className="text-xs text-label-secondary sm:col-span-2">
                Netto-VK (Buchhaltung):{" "}
                <span className="font-medium tabular-nums text-label">
                  {formatCurrency(saleNet)}
                </span>
                {accountHasTaxRate(selectedAccount)
                  ? ` (${taxRate} % MwSt.)`
                  : null}
              </p>
            ) : null}
          </fieldset>

          <fieldset className="space-y-3 rounded-lg border border-separator bg-fill-tertiary/50 p-4">
            <legend className="px-1 text-sm font-semibold text-label">Lager / Charge</legend>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.track_inventory}
                onChange={(e) => setForm((f) => ({ ...f, track_inventory: e.target.checked }))}
              />
              Chargenführung mit MHD
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm">
                {form.track_inventory ? "Menge (Charge)" : "Anfangsbestand"}
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  className="eh-input"
                  value={form.batch_quantity}
                  onChange={(e) => setForm((f) => ({ ...f, batch_quantity: e.target.value }))}
                />
              </label>
              {form.track_inventory ? (
                <>
                  <label className="flex flex-col gap-1 text-sm">
                    Chargen-Nr. (optional)
                    <input
                      className="eh-input"
                      value={form.batch_number}
                      onChange={(e) => setForm((f) => ({ ...f, batch_number: e.target.value }))}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm sm:col-span-2">
                    MHD / Ablaufdatum
                    <input
                      type="date"
                      className="eh-input sm:max-w-xs"
                      value={form.batch_expires_at}
                      onChange={(e) => setForm((f) => ({ ...f, batch_expires_at: e.target.value }))}
                    />
                  </label>
                </>
              ) : null}
            </div>
          </fieldset>

          {error ? <p className="text-sm text-status-red">{error}</p> : null}
          <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              Abbrechen
            </Button>
            <Button type="submit" loading={submitting}>
              Artikel anlegen
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
